import React, { useState } from 'react';
import { string, oneOfType, number, array, object } from 'prop-types';
import { get, map, size, groupBy, isEmpty, find } from 'lodash';
import { Divider } from 'antd';
import moment from 'moment';
import { Main, FlexContainer, Loading, If, Accordion, Row, Text, NotFound, Button, HasPermissions, Icon } from '../../common';
import Filters from '../Resume/Filters';
import GetPickingFeed from '../../graphql/getPickingFeed';
import { isPluralFix } from '../../../utils/misc';
import { InvoicingDateRangeContext } from '../../contexts/InvoicingDateRange';
import { MediaListener } from '../../utils';
import InvoicingCard from './InvoicingCard';
import HorizontalCounters from './HorizontalCounters';
import { Query } from '../../graphql';
import GET_EXTERNAL_PAYMENT_COUNTER from '../../../queries/GET_EXTERNAL_PAYMENT_COUNTER';

const alert = {
    msg: 'Estas órdenes se deben facturar en caja física.',
    color: '#E54717',
    iconNotification: 'alert-circle',
    bgColor: '#F7E6E1',
    borderColor: '#E54717'
};

const TitleComponent = ({ title, totals }) => {
    const label = `${totals} ${isPluralFix(totals, 'pedidos')} por facturar`;
    return (
        <Row align='center'>
            <Text text={title} style={{ minWidth: '120px', fontWeight: 'bold', fontSize: '16px' }} />
            <div style={{ width: '1px', border: '1px solid #eaeaea', margin: '0px 10px', height: '20px' }} />
            <Text text={label} style={{ fontSize: '.75rem', color: '#848484' }} />
        </Row>
    );
};

TitleComponent.propTypes = {
    title: string,
    totals: oneOfType([number, string])
};

TitleComponent.defaultProps = {
    title: 'Sala',
    totals: 0
};

const { msg, bgColor, iconNotification, color, borderColor } = alert;

const InvoicingMonitor = ({ session }) => {
    const [filters, setFilters] = useState({ dateSelected: { init: moment(), end: moment() } });
    const [filtersApplied, setFiltersApplied] = useState(false);
    const userSaleschannels = get(session, 'salesChannels', []);

    const getFilters = () => {
        const dateRange = filtersApplied ? {
            init: `${moment(get(filters, 'dateSelected.init')).utc().format('YYYY-MM-DD')} 00:00:00`,
            end: `${moment(get(filters, 'dateSelected.end')).utc().format('YYYY-MM-DD')} 00:00:00`
        } : {
            init: `${moment().subtract(1, 'months').format('YYYY-MM-DD')} 00:00:00`,
            end: `${moment().add(1, 'days').format('YYYY-MM-DD')} 00:00:00`
        };
        const range = {
            filter: 'shippingETA.init',
            ...dateRange
        };
        const filtersSet = [
            { field: 'salesChannel', value: map(get(filters, 'salesChannels', map(userSaleschannels, 'id'))) },
            { field: 'status', value: get(filters, 'status', [9].map(String)) }
        ];
        const filterSet = { size: 100, filters: filtersSet, range, sort: [{ field: 'shippingETA.init', dir: 'asc' }] };
        return filterSet;
    };

    const getDataBySalesChannels = (data) => groupBy(data, 'salesChannel.name');

    const onSubmitFilters = ({ salesChannels, dateSelected, status, ...values }, { setDate, setSalesChannels }) => {
        setDate(dateSelected);
        setSalesChannels(map(get(salesChannels, 'value', []), ({ value }) => value));
        setFilters({
            salesChannels: map(get(salesChannels, 'value', []), 'value'),
            status: [9].map(String),
            dateSelected,
            ...values
        });
        setFiltersApplied(true);
    };

    const getSalesChannelIdbyLabel = label => parseInt(get(find(userSaleschannels, { name: label }), 'id', '0'));

    const getSalesChannelsIds = data => map(groupBy(data, 'salesChannel.id'), (_, id) => id);

    return (
        <HasPermissions
            to={['invoicing-monitor:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' style={{ minHeight: '90vh' }} />}
        >
            <InvoicingDateRangeContext.Consumer>
                {({ setDate, setSalesChannels }) => {
                    return (
                        <Main background='f4f4f4' withFooter >
                            <MediaListener values={['40%', '100%', '100%']}>
                                {({ currentValue }) => (
                                    <>
                                        <HasPermissions to={['invoicing-monitor:filter&search']}>
                                            <Filters
                                                exclude={['slas', 'status']}
                                                currentValue={currentValue}
                                                onSubmitFilters={values => onSubmitFilters(values, { setDate, setSalesChannels })}
                                                isAuditMonitor
                                            />
                                        </HasPermissions>
                                        <FlexContainer direction='column' shrink={0}>
                                            <GetPickingFeed
                                                variables={{ ...getFilters() }}
                                                fetchPolicy='network-only'
                                                pollInterval={60000}
                                            >
                                                {({ loading, data }) => {
                                                    return (
                                                        <Loading loading={loading}>
                                                            <Query
                                                                query={GET_EXTERNAL_PAYMENT_COUNTER}
                                                                variables={{
                                                                    input: {
                                                                        salesChannel: getSalesChannelsIds(data),
                                                                        from: get(getFilters(), 'range.init'),
                                                                        to: get(getFilters(), 'range.end')
                                                                      }
                                                                }}
                                                                skip={isEmpty(data)}
                                                            >
                                                                {({ data: externalPaymentCounterData, loading: loadingCounters }) => {
                                                                    const counters = get(externalPaymentCounterData, 'externalPaymentCounter.payload', []);

                                                                    return (<>
                                                                        {!isEmpty(getDataBySalesChannels(data)) && <Row padding={10}>
                                                                            <Row></Row>
                                                                            <Row align='center' style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8, padding: '9px 12px', width: 350 }}>
                                                                                <Icon name={iconNotification} size={24} color={color} />
                                                                                <Divider type='vertical' style={{ background: color, height: '1.5em', margin: '0px 16px 0px 14px' }} />
                                                                                <Text text={msg} size={12} color='#323A44' strong />
                                                                            </Row>
                                                                        </Row>}
                                                                        <If
                                                                            condition={size(data)}
                                                                            then={map(getDataBySalesChannels(data), (items, label) => (
                                                                                <Accordion
                                                                                    key={label}
                                                                                    title={<TitleComponent title={label} totals={size(items)} />}
                                                                                    background='#f4f4f4'
                                                                                    closedBackground='white'
                                                                                    defaultOpened={size(items) > 0}
                                                                                    rightHeader={
                                                                                        <Loading loading={loadingCounters}>
                                                                                            <FlexContainer>
                                                                                                <HorizontalCounters data={find(counters, { id: getSalesChannelIdbyLabel(label) })} />
                                                                                                <Button
                                                                                                    secondary
                                                                                                    text='Imprimir todas'
                                                                                                    minWidth='130px'
                                                                                                    onClick={() =>
                                                                                                        window.open(`/invoicing-orders/${JSON.stringify(map(items, ({ id, assignInfo }) => ({ id, code: get(assignInfo, 'eanOrder', 0) })))}`, '_blank')
                                                                                                    }
                                                                                                />
                                                                                            </FlexContainer>
                                                                                        </Loading>
                                                                                    }
                                                                                >
                                                                                    <MediaListener values={[{}, {}, { overflowX: 'scroll', flexWrap: 'nowrap' }]}>
                                                                                        {({ currentValue }) => (
                                                                                            <Row style={currentValue}>
                                                                                                { map(items, (item, key) => <InvoicingCard session={session} {...item} key={key} />) }
                                                                                            </Row>
                                                                                        )}
                                                                                    </MediaListener>
                                                                                </Accordion>
                                                                            ))}
                                                                            else={(<NotFound firstTextLine='No se encontraron ordenes' icon='cross' iconBackgroundColor='#eaeaea' />)}
                                                                        />
                                                                    </>);
                                                                }}
                                                            </Query>
                                                        </Loading>
                                                    );
                                                }}
                                            </GetPickingFeed>
                                        </FlexContainer>
                                    </>
                                )}
                            </MediaListener>
                        </Main>
                    );
                }}
            </InvoicingDateRangeContext.Consumer>
        </HasPermissions>
    );
};

InvoicingMonitor.propTypes = {
    session: oneOfType([array, object])
};

InvoicingMonitor.defaultProps = {
    session: {}
};

export default InvoicingMonitor;
